/**
 * The billing page: where the workspace stands, the plans it can buy, and the way to Stripe.
 *
 * Choosing a plan opens Stripe Checkout; managing card, invoices, plan changes and cancellation
 * happens in Stripe's Customer Portal. Neither page is ours, so this one only sends the browser
 * there and shows the result when Stripe sends it back with `?checkout=done` or `canceled`.
 */

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Notice } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { SettingsHeader } from "@/features/settings/SettingsHeader";
import { cn } from "@/lib/cn";
import { formatMomentDate } from "@/lib/dates";
import { formatMoney } from "@/lib/money";
import { billingQuery, useCheckout, usePortal, type Billing, type PlanOffer } from "./queries";
import { statusLine } from "./status";
import { UpgradeLink } from "./UpgradeLink";

/** Back from Checkout, ask every two seconds for up to half a minute while the webhook lands. */
const POLL_MS = 2_000;
const MAX_POLLS = 15;

export function BillingPage() {
  const { t } = useTranslation();
  const { checkout } = useSearch({ from: "/app/settings/billing" });
  const queryClient = useQueryClient();
  const me = useQuery(meQuery);
  const billing = useQuery({
    ...billingQuery,
    refetchInterval: (query) =>
      checkout === "done" &&
      !query.state.data?.subscribed &&
      query.state.dataUpdateCount <= MAX_POLLS
        ? POLL_MS
        : false,
  });
  const checkoutMutation = useCheckout();
  const portal = usePortal();

  // A new plan changes what the rest of the app allows, and `/me` is what it reads that from.
  const subscribed = billing.data?.subscribed;
  const planCode = billing.data?.plan_code;
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
  }, [subscribed, planCode, queryClient]);

  if (billing.isPending) return <p className="text-stone-500">{t("common.loading")}</p>;
  if (billing.isError) return <Notice tone="error">{t("billing.loadFailed")}</Notice>;

  const data = billing.data;
  const timeZone = me.data?.workspace?.timezone ?? "UTC";
  const failure = checkoutMutation.error ?? portal.error;
  const busy = checkoutMutation.isPending || portal.isPending;

  return (
    <section className="space-y-5">
      <SettingsHeader title={t("billing.title")} description={t("billing.subtitle")} />

      {checkout === "done" &&
        (data.subscribed ? (
          <Notice tone="success">{t("billing.checkout.done")}</Notice>
        ) : (
          <Notice tone="info">{t("billing.checkout.waiting")}</Notice>
        ))}
      {checkout === "canceled" && <Notice tone="info">{t("billing.checkout.canceled")}</Notice>}

      <CurrentPlan billing={data} timeZone={timeZone} />

      {data.has_billing_account && (
        <Button variant="secondary" disabled={busy} onClick={() => portal.mutate()}>
          {portal.isPending ? t("billing.opening") : t("billing.manage")}
        </Button>
      )}

      {failure && (
        <Notice tone="error">
          {failure instanceof ApiError ? failure.message : t("billing.openFailed")}
        </Notice>
      )}

      <div className="space-y-3">
        <h2 className="font-medium text-stone-800">{t("billing.plans")}</h2>
        <ul className="space-y-3">
          {data.plans.map((plan) => (
            <PlanCard
              key={plan.code}
              plan={plan}
              billing={data}
              busy={busy}
              choosing={checkoutMutation.isPending && checkoutMutation.variables === plan.code}
              onChoose={() => checkoutMutation.mutate(plan.code)}
              onManage={() => portal.mutate()}
            />
          ))}
        </ul>
      </div>

      <p className="text-sm text-stone-500">{t("billing.stripeNote")}</p>
    </section>
  );
}

function CurrentPlan({ billing, timeZone }: { billing: Billing; timeZone: string }) {
  const { t } = useTranslation();
  const line = statusLine(billing);
  const current = billing.plans.find((plan) => plan.code === billing.plan_code);
  const pending = billing.plans.find((plan) => plan.code === billing.pending_plan_code);
  const date = "date" in line ? formatMomentDate(line.date, timeZone) : undefined;
  const warn = line.key === "pastDue" || line.key === "lapsed" || line.key === "trialEnded";
  // Leaving, or gone: the full download PRODUCT.md promises is offered here.
  const leaving = line.key === "ending" || line.key === "lapsed" || line.key === "trialEnded";

  return (
    <div
      className={cn(
        "space-y-2 rounded-lg border p-4",
        warn ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-white",
      )}
    >
      {current && (
        <p className="text-sm text-stone-500">{t("billing.current", { plan: current.name })}</p>
      )}
      <p className="text-stone-800">{t(`billing.status.${line.key}`, { date })}</p>
      {pending && billing.pending_plan_at && (
        <p className="text-sm text-stone-700">
          {t("billing.pending", {
            plan: pending.name,
            date: formatMomentDate(billing.pending_plan_at, timeZone),
          })}
          {current?.image_retention && !pending.image_retention && (
            <> {t("billing.pendingPhotos", { plan: current.name })}</>
          )}
        </p>
      )}
      {(leaving || pending) && (
        <p className="text-sm">
          <UpgradeLink to="/settings/data" label={t("billing.downloadEverything")} />
        </p>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  billing,
  busy,
  choosing,
  onChoose,
  onManage,
}: {
  plan: PlanOffer;
  billing: Billing;
  busy: boolean;
  choosing: boolean;
  onChoose: () => void;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const current = plan.code === billing.plan_code && !billing.read_only;
  const price =
    plan.price_minor !== null && plan.currency
      ? t("billing.perMonth", {
          price: formatMoney({ minor: plan.price_minor, currency: plan.currency }),
        })
      : t("billing.noPrice");

  return (
    <li
      className={cn(
        "space-y-3 rounded-lg border bg-white p-4",
        current ? "border-brand-700" : "border-stone-200",
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-lg font-semibold text-stone-900">{plan.name}</h3>
        {current && (
          <span className="bg-brand-50 text-brand-700 rounded-full px-2 py-0.5 text-xs font-medium">
            {t("billing.currentBadge")}
          </span>
        )}
      </div>
      <p className="text-stone-800">{price}</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-stone-600">
        <li>
          {plan.monthly_receipt_cap === null
            ? t("billing.features.receiptsUnlimited")
            : t("billing.features.receipts", { count: plan.monthly_receipt_cap })}
        </li>
        <li>
          {plan.business_cap === null
            ? t("billing.features.businessesUnlimited")
            : t("billing.features.businesses", { count: plan.business_cap })}
        </li>
        <li>
          {plan.image_retention
            ? t("billing.features.photosKept")
            : t("billing.features.photosDeleted")}
        </li>
      </ul>
      <PlanAction
        plan={plan}
        billing={billing}
        current={current}
        busy={busy}
        choosing={choosing}
        onChoose={onChoose}
        onManage={onManage}
      />
    </li>
  );
}

/**
 * One button per plan. Without a Stripe subscription, any plan with a price can be bought. With
 * one, Checkout would start a second subscription, so a change of plan goes through the portal.
 */
function PlanAction({
  plan,
  billing,
  current,
  busy,
  choosing,
  onChoose,
  onManage,
}: {
  plan: PlanOffer;
  billing: Billing;
  current: boolean;
  busy: boolean;
  choosing: boolean;
  onChoose: () => void;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  if (billing.subscribed) {
    if (current) return null;
    if (plan.code === billing.pending_plan_code) {
      return <p className="text-sm font-medium text-stone-700">{t("billing.booked")}</p>;
    }
    return (
      <Button variant="secondary" disabled={busy} onClick={onManage}>
        {t("billing.switchInPortal", { plan: plan.name })}
      </Button>
    );
  }
  return (
    <Button disabled={busy || !plan.available} onClick={onChoose}>
      {choosing ? t("billing.opening") : t("billing.choose", { plan: plan.name })}
    </Button>
  );
}
